// unmanaged
// Server Status → Scheduled jobs (continuous_mfa/jobs.py): recent runs, and
// the weekly project summary's preview and send-now. Admin only.
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import apiClient from "../utils/apiClient";
import DataCard, { tableHeaderCellSx } from "../components/DataCard";
import { formatTime } from "./projectParts";

const STATUS_COLOR = { completed: "success", error: "error", running: "info" };

function resultText(run) {
  if (run.error) return run.error;
  const r = run.result || {};
  if (r.skipped) return r.skipped;
  if (r.sent_to !== undefined) return `emailed ${r.sent_to}, ${r.items} item${r.items === 1 ? "" : "s"}, ${r.renewals} renewal${r.renewals === 1 ? "" : "s"}`;
  return "";
}

export default function ScheduledJobsCard() {
  const [runs, setRuns] = useState([]);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRuns((await apiClient.get("/job-run-list")) || []);
    } catch (e) {
      setMessage({ type: "error", text: e?.message || "Could not load scheduled jobs" });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const action = (name) => apiClient.post("/project-request", { action: name });

  const openPreview = async () => {
    try {
      setPreview((await action("summary_preview")).records[0]);
    } catch (e) {
      setMessage({ type: "error", text: e?.message || "Could not build the summary" });
    }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const run = (await action("summary_send")).records[0];
      setPreview(null);
      setMessage({ type: run.status === "error" ? "error" : "success", text: `Weekly summary: ${resultText(run) || run.status}` });
      await load();
    } catch (e) {
      setMessage({ type: "error", text: e?.message || "Could not send" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <DataCard title="Scheduled jobs" action={<Button size="small" onClick={openPreview}>Weekly summary…</Button>}>
      {message && <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 1 }}>{message.text}</Alert>}
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
        The weekly project summary goes to every Admin by email and to Google Chat, Mondays at 8:00 am Eastern,
        when the server's jobs_enabled setting is on.
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderCellSx}>Run</TableCell>
              <TableCell sx={tableHeaderCellSx}>Started</TableCell>
              <TableCell sx={tableHeaderCellSx}>Status</TableCell>
              <TableCell sx={tableHeaderCellSx}>Result</TableCell>
              <TableCell sx={tableHeaderCellSx}>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.length === 0 ? (
              <TableRow><TableCell colSpan={5} sx={{ color: "text.secondary" }}>No runs yet.</TableCell></TableRow>
            ) : runs.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.job} · {r.period}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTime(r.started_at)}</TableCell>
                <TableCell><Chip size="small" color={STATUS_COLOR[r.status] || "default"} label={r.status} /></TableCell>
                <TableCell>{resultText(r)}</TableCell>
                <TableCell>{r.triggered_by || `schedule (${r.host})`}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={!!preview} onClose={() => setPreview(null)} fullWidth maxWidth="md">
        <DialogTitle>{preview?.subject}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            To: {(preview?.recipients || []).join(", ") || "no Admins"}
          </Typography>
          {preview?.empty ? (
            <Typography variant="body2">Nothing to report this week, so nothing would be sent.</Typography>
          ) : (
            <Box component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: "0.85rem", m: 0 }}>
              {preview?.text}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
          <Button variant="contained" onClick={sendNow} disabled={busy || preview?.empty}>Send now</Button>
        </DialogActions>
      </Dialog>
    </DataCard>
  );
}
